import { apiCweb } from './api-cweb';
import { DbCommit, DbFolderPermission, DbSecurityGroup } from '~src/types/db';
import { appLog } from '~src/utils/log';

interface LoadSecurityGroupsParams {
  projectId: string;
}
export const loadSecurityGroups = async (
  key,
  { projectId }: LoadSecurityGroupsParams
): Promise<DbSecurityGroup[]> => {
  appLog('loadSecurityGroups ->', { projectId });
  const res = await apiCweb.get<DbSecurityGroup[]>(
    `/projects/${projectId}/security_groups`
  );

  if (!res.data) throw new Error('Missing response data');
  return res.data;
};

interface LoadFolderPermissionsParams {
  projectId: string;
  folderId: string;
}
export const loadFolderPermissions = async (
  key,
  { projectId, folderId }: LoadFolderPermissionsParams
): Promise<DbFolderPermission[]> => {
  appLog('loadFolderPermissions ->', { projectId, folderId });
  const res = await apiCweb.get<DbFolderPermission[]>(
    `/projects/${projectId}/folder_permissions`,
    { params: { folder_id: folderId } }
  );

  return res.data;
};

interface LoadFoldersPermissionsParams {
  projectId: string;
  folderIds: string[];
}

export const loadFoldersPermissions = async (
  key,
  { projectId, folderIds }: LoadFoldersPermissionsParams
) => {
  const chunkSize = 200;
  const folderIdChunks: string[][] = [];
  for (let i = 0; i < folderIds.length; i += chunkSize) {
    folderIdChunks.push(folderIds.slice(i, i + chunkSize));
  }

  const results = await Promise.all(
    folderIdChunks.map(async (chunk) => {
      const res = await apiCweb.get<DbFolderPermission[]>(
        `/projects/${projectId}/folder_permissions`,
        {
          params: {
            folder_ids: chunk.join(','),
          },
        }
      );
      return res.data;
    })
  );

  const flattenedResults = results.flat();

  return flattenedResults;
};

export interface CreateFolderPermissionParams {
  projectId: string;
  permissionData: Pick<
    DbFolderPermission,
    'level' | 'folder_id' | 'security_group_id'
  >;
  commit: DbCommit;
}
export const createFolderPermission = async ({
  projectId,
  permissionData,
  commit,
}: CreateFolderPermissionParams): Promise<DbFolderPermission> => {
  appLog('createFolderPermission ->', { projectId, permissionData });
  const res = await apiCweb.post<DbFolderPermission>(
    `/projects/${projectId}/folder_permissions`,
    { ...permissionData },
    { params: { commit_id: commit.id } }
  );
  return res.data;
};

export interface UpdateFolderPermission {
  projectId: string;
  permission: DbFolderPermission;
  commit: DbCommit;
}
export const updateFolderPermission = async ({
  projectId,
  permission,
  commit,
}: UpdateFolderPermission): Promise<DbFolderPermission> => {
  appLog('updateFolderPermission ->', { projectId, permission });
  const res = await apiCweb.put<DbFolderPermission>(
    `/projects/${projectId}/folder_permissions/${permission.id}`,
    { ...permission },
    { params: { commit_id: commit.id } }
  );
  return res.data;
};

export interface DeleteFolderPermissionParams {
  projectId: string;
  permissionId: DbFolderPermission['id'];
  commit: DbCommit;
}
export const deleteFolderPermission = async ({
  projectId,
  permissionId,
  commit,
}: DeleteFolderPermissionParams): Promise<DbFolderPermission> => {
  appLog('deleteFolderPermission ->', { projectId, permissionId });
  const res = await apiCweb.delete<DbFolderPermission>(
    `/projects/${projectId}/folder_permissions/${permissionId}`,
    { params: { commit_id: commit.id } }
  );
  return res.data;
};
